import { noop } from 'lodash';
import { memo, useMemo } from 'react';

import { SegmentedControl, type SegmentedControlOption } from '@air/component-segmented-control';
import { cn } from '@air/utils-tailwind';

import { UpsellFlashIconButton } from '~/components/SharePrivateClipModalV2/UpsellFlashIconButton';
import { useSharePrivateCollectionModal } from '~/components/SharePrivateCollectionModal/SharePrivateCollectionModalContext';
import { SHARE_MODAL_UPSELL_MESSAGE } from '~/constants/upsells';

export type PermissionSegmentFieldProps = {
  label: string;
  value: string;
  onSelect: (option: SegmentedControlOption) => void;
  options: SegmentedControlOption[];
  disabled?: boolean;
  requiresFeature: boolean;
};

export const PermissionSegmentField = memo(
  ({ label, value, onSelect, options, disabled, requiresFeature }: PermissionSegmentFieldProps) => {
    const { isSaving } = useSharePrivateCollectionModal();
    const isDisabled = disabled ?? isSaving;
    const selectedOption = useMemo(() => options.find((option) => option.id === value) ?? options[0], [options, value]);

    return (
      <div className="flex w-full items-center justify-between py-1">
        <div className="mt-1 flex items-center">
          <div className="mr-2 text-16 text-grey-12">{label}</div>
          {requiresFeature && <UpsellFlashIconButton>{SHARE_MODAL_UPSELL_MESSAGE}</UpsellFlashIconButton>}
        </div>
        <div className={cn('flex flex-row items-center gap-2', isDisabled || requiresFeature ? 'opacity-50' : '')}>
          <SegmentedControl
            size="small"
            color="blue"
            outerColor="grey"
            options={options}
            selectedOption={selectedOption}
            onSelect={isDisabled || requiresFeature ? noop : onSelect}
          />
        </div>
      </div>
    );
  },
);

PermissionSegmentField.displayName = 'PermissionSegmentField';
